// @ts-nocheck
// Monitor / Whisper / Barge for the team-lead dashboard. Wraps the same
// snoop start/stop endpoints the standalone supervisor board used: one
// session at a time, only while the target agent's call is ANSWERED and has
// an agentCallId, and only for a team lead logged in on a profile.
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { deleteApiCallWithBody, postApiCall } from '@/service/httpCall';
import { getApiCall } from '@/service/getApiCall';
import { API_AGENT_SNOOP_START, API_AGENT_SNOOP_STOP, API_AGENT_UPDATE } from '@/service/url';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { getUser } from '@/utilities/setting';
import { readTranscriptionEnabled } from '@/utilities/callCentreTranscriptionAcl';
import { useCallStore } from '@/router/zustand/callStore';

export const SNOOP_MODES = [
  { mode: 'monitor', label: 'Monitor', tone: 'call' },
  { mode: 'whisper', label: 'Whisper', tone: 'warn' },
  { mode: 'barge', label: 'Barge', tone: 'crit' },
];

function listener() {
  const me = getUser();
  const myAgent = me?.agentData || {};
  const profiles = myAgent?.agentTelephony?.profile || [];
  const active = profiles.find((p) => p.active_profile === true);
  return { teamLeadAgentId: myAgent?.id, listenerExt: active?.number?.number };
}

export function useSnoop(allowed = true) {
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);

  const hasListener = Boolean(listener().listenerExt);

  const start = useCallback(
    async (mode, row) => {
      if (!allowed) return;
      const { ready, callId } = row?.snoop || {};
      if (!ready) return;

      const { teamLeadAgentId, listenerExt } = listener();
      if (!teamLeadAgentId) {
        toast.error('Team lead agent not found');
        return;
      }
      if (!listenerExt) {
        toast.error('Connect your own phone (WebRTC/SIP) via the Dialer first to listen in.');
        return;
      }

      const asteriskId = row?.dashboard?.asterisk_id || '1';
      setBusy(true);
      try {
        const url = `${API_AGENT_SNOOP_START.replace(':id', encodeURIComponent(callId))}?asterisk_id=${encodeURIComponent(asteriskId)}`;
        const data = await postApiCall(url, {
          to: String(listenerExt),
          mode,
          from: String(listenerExt),
          snoop_agent_id: row.agent?.id,
          team_lead_agent_id: teamLeadAgentId,
          // pbx_name: useCallStore.getState().pbxName || null,
        });
        if (data?.pbx_name) {
          useCallStore.getState().setSipData({ pbxName: String(data.pbx_name) });
        }
        setSession({
          agentId: row.agent?.id,
          mode,
          conferenceId: data?.listenerAgentConferenceId || null,
        });
        // Best-effort context for the script-fetch effect in
        // AgentPortalPage.tsx — the SSE roster row only ever carries the
        // agent's static team-lead-configured group (row.groupId), not
        // their live call's actual campaign, but that's still a monitored-
        // agent-relevant script instead of the lead's own unrelated one.
        useCallStore.getState().setActiveSnoopContext({
          agentId: row.agent?.id,
          groupId: row.groupId || row.dashboard?.call_group_id || null,
        });
        if (row.agent?.id) {
          getApiCall(`${API_AGENT_UPDATE}/${row.agent.id}`)
            .then((agentData) => {
              const current = useCallStore.getState().activeSnoopContext;
              if (current && current.agentId === row.agent.id) {
                useCallStore.getState().setActiveSnoopContext({
                  ...current,
                  isTranscriptionEnabled: readTranscriptionEnabled(agentData),
                });
              }
            })
            .catch(() => {});
        }
        toast.success(`${mode} started — ${row.name}`);
      } catch (err) {
        toast.error(friendlyApiError(err) || `${mode} failed`);
      } finally {
        setBusy(false);
      }
    },
    [allowed],
  );

  const stop = useCallback(async () => {
    if (!session) return;
    const { teamLeadAgentId, listenerExt } = listener();
    setBusy(true);
    try {
      if (session.conferenceId) {
        const url = `${API_AGENT_SNOOP_STOP.replace(':id', encodeURIComponent(session.conferenceId))}?asterisk_id=1`;
        await deleteApiCallWithBody(url, {
          from: listenerExt ? String(listenerExt) : null,
          snoop_agent_id: session.agentId,
          team_lead_agent_id: teamLeadAgentId,
          pbx_name: useCallStore.getState().pbxName || null,
        });
      }
      toast.success('Snoop stopped');
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Stop snoop failed');
    } finally {
      setSession(null);
      setBusy(false);
      useCallStore.getState().setActiveSnoopContext(null);
    }
  }, [session]);

  // No-API-call reset — for when the monitored call has already ended/
  // dropped out from under an active snoop session (agent hung up, went
  // offline, etc). Calling stop()'s DELETE endpoint against a conference
  // that's already gone is pointless at best; this just clears the stuck
  // "Stop {mode}" pressed state so the buttons go back to their defaults.
  const reset = useCallback(() => {
    setSession(null);
    setBusy(false);
    useCallStore.getState().setActiveSnoopContext(null);
  }, []);

  return useMemo(
    () => ({
      session,
      busy,
      hasListener,
      start,
      stop,
      reset,
      isActive: (agentId, mode) =>
        Boolean(session) && session.agentId === agentId && session.mode === mode,
      canUse: (row, mode) => {
        if (!allowed || !hasListener || busy || !row?.snoop?.ready) return false;
        if (!session) return true;
        return session.agentId === row.agent?.id && session.mode === mode;
      },
    }),
    [session, busy, hasListener, start, stop, reset, allowed],
  );
}

export default useSnoop;
